import { useTranslate } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import { Form, Select } from 'antd';
import { BannerActionKind } from '@/api/generated/schemas';
import {
  isTargetless,
  NOT_DISABLED,
  TARGETABLE_COLLECTION_KINDS,
} from './bannerActionTarget';

interface BannerActionTargetFieldProps {
  kind?: BannerActionKind;
  /** Stored target of the edited record, so its option label can be fetched. */
  currentTargetRef?: string | null;
}

export const BannerActionTargetField = ({
  kind,
  currentTargetRef,
}: BannerActionTargetFieldProps) => {
  const t = useTranslate();

  // Hooks run unconditionally (rules of hooks); the queries are gated
  // per kind instead. Slug-valued selects (book, category) skip stored-value
  // label resolution — getMany works for id-valued selects only, so a stored
  // slug shows raw until searched.
  const { selectProps: publicationProps } = useSelect({
    resource: 'user_catalog.publications',
    optionLabel: 'title',
    optionValue: 'slug',
    filters: [NOT_DISABLED],
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    queryOptions: { enabled: kind === BannerActionKind.book },
  });

  const { selectProps: collectionProps } = useSelect({
    resource: 'user_catalog.collections',
    optionLabel: 'name_with_collection_kind',
    optionValue: 'id',
    filters: [
      {
        field: 'collection_kind',
        operator: 'in',
        value: TARGETABLE_COLLECTION_KINDS,
      },
      NOT_DISABLED,
    ],
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    defaultValue:
      kind === BannerActionKind.collection &&
      /^\d+$/.test(currentTargetRef ?? '')
        ? Number(currentTargetRef)
        : undefined,
    queryOptions: { enabled: kind === BannerActionKind.collection },
  });

  const { selectProps: categoryProps } = useSelect({
    resource: 'user_catalog.categories',
    optionLabel: 'name',
    optionValue: 'slug',
    filters: [NOT_DISABLED],
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    queryOptions: { enabled: kind === BannerActionKind.category },
  });

  if (isTargetless(kind)) return null;

  const selectPropsByKind = {
    [BannerActionKind.book]: publicationProps,
    [BannerActionKind.collection]: {
      ...collectionProps,
      // target_ref is a string on the wire; align option values with it
      options: (collectionProps.options ?? []).map((option) => ({
        ...option,
        value: String(option.value),
      })),
    },
    [BannerActionKind.category]: {
      ...categoryProps,
      // categories without a slug cannot be linked
      options: (categoryProps.options ?? []).filter(
        (option) => option.value != null
      ),
    },
  }[kind];

  return (
    <Form.Item
      name="target_ref"
      label={t('user_catalog.banner_actions.fields.target')}
      rules={[{ required: true }]}
    >
      <Select
        {...selectPropsByKind}
        showSearch
        filterOption={false}
        placeholder={t(
          `user_catalog.banner_actions.target_placeholder.${kind}`
        )}
      />
    </Form.Item>
  );
};
